import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import {
  GetCheapestItineraryWithStatusDocument,
  GetCheapestItineraryWithStatusQuery,
  GetThreadAiDocument,
  GetThreadAiQuery,
  Maybe,
  ValidateRecipeDocument,
  ValidateRecipeQuery,
  ValidateRecipeQueryVariables,
} from '../../data/smart-search/hooks';
import { initializeSmartSearchApolloClient } from '../../data/smart-search/provider';
import {
  AiThreadResponseQl,
  CheapestItineraryStatusType,
  ItineraryResponseQl,
  RecipeQl,
  ThreadAiRequestQl,
} from '../../dtos/smart-search/types';
import { environment } from '../../environment.prod';
import { isSSR } from '../../utils/helpers';

class SmartSearchServiceClass {
  // TODO: For now, we only use this for the AI flow
  //       but we might want to reuse a single session id across all pages
  private recipeSessionId = undefined;

  private apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

  private getWindowHrefWithoutQueryParameters = () =>
    `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

  addSmartSearchTrackingMetadataToRecipe = (recipe: RecipeQl) => ({
    ...recipe,
    metadata: {
      sourceType: 'MarketPlace',
      originUrl: isSSR()
        ? 'server'
        : this.getWindowHrefWithoutQueryParameters(),
    },
  });

  wrapWithSessionId = (payload: Record<string, unknown>) => ({
    ...payload,
    sessionId: this.recipeSessionId,
  });

  // TODO: For now, we only use this for the AI flow
  //       but we might want to reuse a single session id across all pages
  reusePreviousAiResponseSessionId = (
    aiResponse: AiThreadResponseQl
  ): AiThreadResponseQl => {
    const parsedRecipe = JSON.parse(aiResponse?.recipeJson ?? '{}');

    if (!this.recipeSessionId) {
      this.recipeSessionId = parsedRecipe.sessionId;
    }

    return {
      ...aiResponse,
      recipeJson: JSON.stringify({
        ...parsedRecipe,
        sessionId: this.recipeSessionId,
      }),
    };
  };

  getSmartSearchApolloClient = (smartSearchApi?: string) => {
    if (!this.apolloClient) {
      this.apolloClient = initializeSmartSearchApolloClient(
        smartSearchApi ?? environment.smartSearchApi
      );
    }

    return this.apolloClient;
  };

  getItinerary = async (
    recipe: RecipeQl,
    smartSearchApi: string
  ): Promise<{
    status: CheapestItineraryStatusType | undefined;
    failureReason?: string | null;
    itinerary: Maybe<ItineraryResponseQl>;
  }> => {
    const smartSearchApolloClient =
      this.getSmartSearchApolloClient(smartSearchApi);

    const smartSearch =
      await smartSearchApolloClient.query<GetCheapestItineraryWithStatusQuery>({
        query: GetCheapestItineraryWithStatusDocument,
        variables: {
          recipe: this.addSmartSearchTrackingMetadataToRecipe(recipe),
        },
      });

    return {
      status: smartSearch.data.cheapestItineraryWithStatus?.status,
      failureReason:
        smartSearch.data.cheapestItineraryWithStatus?.failureReason,
      itinerary: smartSearch.data.cheapestItineraryWithStatus
        ?.itinerary as Maybe<ItineraryResponseQl>,
    };
  };

  getThreadAi = async (
    request: ThreadAiRequestQl,
    smartSearchApi: string
  ): Promise<AiThreadResponseQl> => {
    const smartSearchApolloClient =
      this.getSmartSearchApolloClient(smartSearchApi);

    const smartSearch = await smartSearchApolloClient.query<GetThreadAiQuery>({
      query: GetThreadAiDocument,
      variables: { request: { ...request, sessionId: this.recipeSessionId } },
    });

    return smartSearch.data.getThreadAI as AiThreadResponseQl;
  };

  validateRecipe = async (
    recipe: RecipeQl,
    smartSearchApi: string
  ): Promise<boolean> => {
    const smartSearchApolloClient =
      this.getSmartSearchApolloClient(smartSearchApi);

    const smartSearch = await smartSearchApolloClient.query<
      ValidateRecipeQuery,
      ValidateRecipeQueryVariables
    >({
      query: ValidateRecipeDocument,
      variables: {
        recipe,
      },
    });
    return !!smartSearch.data.validateRecipe;
  };
}

export const SmartSearchService = new SmartSearchServiceClass();
